import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { StaffLeavesAPI } from "@/lib/staff-attendance-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plane, Inbox, Wallet, Plus, Check, X, Loader2, CalendarDays } from "lucide-react";

const LEAVE_TYPES = [
  { v: "CL", label: "Casual (CL)" },
  { v: "SL", label: "Sick (SL)" },
  { v: "PL", label: "Privilege (PL)" },
  { v: "COMP", label: "Comp Off" },
  { v: "UNPAID", label: "Unpaid" },
];

const APPROVE_ROLES = ["super_admin", "tenant_admin", "inst_admin", "campus_admin", "principal", "hr"];
const STATUS_CLS = {
  pending: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  rejected: "bg-rose-500/10 text-rose-700 border-rose-500/20",
  cancelled: "bg-zinc-500/10 text-zinc-600 border-zinc-500/20",
};

export default function StaffLeaves() {
  const { user } = useAuth();
  const isApprover = (user?.roles || []).some((r) => APPROVE_ROLES.includes(r));

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto py-8 px-4" data-testid="leaves-page">
        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center">
              <Plane className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display font-extrabold text-2xl tracking-tight">Leaves</h1>
              <p className="text-sm text-muted-foreground">Request time off · track balances · approvals</p>
            </div>
          </div>
        </header>

        <Tabs defaultValue={isApprover ? "inbox" : "mine"}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="tab-mine"><Plane className="h-3.5 w-3.5 mr-1.5" />My Leaves</TabsTrigger>
            {isApprover && <TabsTrigger value="inbox" data-testid="tab-inbox"><Inbox className="h-3.5 w-3.5 mr-1.5" />Approvals</TabsTrigger>}
          </TabsList>
          <TabsContent value="mine" className="mt-6"><MinePanel /></TabsContent>
          {isApprover && <TabsContent value="inbox" className="mt-6"><InboxPanel /></TabsContent>}
        </Tabs>
      </div>
    </AppShell>
  );
}

// ─── My leaves ────────────────────────────────────────────────────────

function MinePanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-leaves"], queryFn: () => StaffLeavesAPI.mine() });
  const [dialogOpen, setDialogOpen] = useState(false);

  if (q.isLoading) return <Loading />;
  const d = q.data;
  if (!d?.staff_id) {
    return <div className="p-8 text-center text-muted-foreground bg-card rounded-2xl" data-testid="leaves-no-profile">
      Your login isn't linked to a staff profile. Ask your admin to link your account before applying.
    </div>;
  }
  const bal = d.balances?.balances || {};
  const used = d.balances?.used || {};
  return (
    <div className="space-y-6" data-testid="mine-panel">
      {/* Balance cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {["CL", "SL", "PL", "COMP"].map((k) => (
          <div key={k} className="bg-card rounded-2xl shadow-sm p-4" data-testid={`bal-${k}`}>
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{k}</div>
              <div className="text-xs text-muted-foreground">of {bal[k] || 0}</div>
            </div>
            <div className="font-display font-extrabold text-3xl tracking-tight mt-1 text-violet-600">
              {(bal[k] || 0) - (used[k] || 0)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">days remaining</div>
          </div>
        ))}
      </div>

      {/* Apply button */}
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-lg">Recent requests</div>
          <div className="text-xs text-muted-foreground">Your leaves for {d.balances?.year || new Date().getFullYear()}</div>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="btn-apply-leave"><Plus className="h-4 w-4 mr-1.5" />Apply for Leave</Button>
      </div>

      {/* Leave list */}
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead>Days</TableHead>
            <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.leaves.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No leave requests yet.</TableCell></TableRow>}
            {d.leaves.map((l) => <LeaveRow key={l.id} l={l} onChange={() => qc.invalidateQueries({ queryKey: ["my-leaves"] })} allowCancel />)}
          </TableBody>
        </Table>
      </div>

      {dialogOpen && <ApplyDialog onClose={() => setDialogOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["my-leaves"] })} />}
    </div>
  );
}

function ApplyDialog({ onClose, onDone }) {
  const [leaveType, setLeaveType] = useState("CL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const mut = useMutation({
    mutationFn: () => StaffLeavesAPI.request({ leave_type: leaveType, from_date: fromDate, to_date: toDate, reason }),
    onSuccess: () => { toast.success("Leave request submitted"); onDone(); onClose(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const days = fromDate && toDate ? Math.max(0, Math.round((new Date(toDate) - new Date(fromDate)) / 86400000) + 1) : 0;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="apply-leave-dialog">
        <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Leave Type</label>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">From</label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="leave-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">To</label>
              <Input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} data-testid="leave-to" />
            </div>
          </div>
          {days > 0 && <div className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {days} day{days > 1 ? "s" : ""}</div>}
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Reason (optional)</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leave-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!fromDate || !toDate || days === 0 || mut.isPending}
            onClick={() => mut.mutate()}
            data-testid="leave-submit"
          >
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Approvals inbox ─────────────────────────────────────────────────

function InboxPanel() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const q = useQuery({ queryKey: ["leaves-inbox", status], queryFn: () => StaffLeavesAPI.inbox(status) });
  if (q.isLoading) return <Loading />;
  return (
    <div className="space-y-4" data-testid="inbox-panel">
      <div className="flex items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="inbox-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">{q.data?.length ?? 0} request{(q.data?.length ?? 0) === 1 ? "" : "s"}</div>
      </div>
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Staff</TableHead><TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead>Days</TableHead>
            <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(q.data || []).length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No {status} leaves.</TableCell></TableRow>}
            {(q.data || []).map((l) => <LeaveRow key={l.id} l={l} showStaff onChange={() => qc.invalidateQueries({ queryKey: ["leaves-inbox"] })} allowDecide />)}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function LeaveRow({ l, onChange, showStaff, allowCancel, allowDecide }) {
  const approve = useMutation({
    mutationFn: (note) => StaffLeavesAPI.approve(l.id, note),
    onSuccess: () => { toast.success("Approved"); onChange(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const reject = useMutation({
    mutationFn: (note) => StaffLeavesAPI.reject(l.id, note),
    onSuccess: () => { toast.success("Rejected"); onChange(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const cancel = useMutation({
    mutationFn: () => StaffLeavesAPI.cancel(l.id),
    onSuccess: () => { toast.success("Cancelled"); onChange(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <TableRow data-testid={`leave-${l.id}`}>
      {showStaff && <TableCell className="font-medium">{l.staff_name}</TableCell>}
      {!showStaff && <TableCell><span className="text-xs text-muted-foreground font-mono">{l.id.slice(-6)}</span></TableCell>}
      <TableCell><Badge variant="outline">{l.leave_type}</Badge></TableCell>
      <TableCell className="font-mono text-xs">{l.from_date} → {l.to_date}</TableCell>
      <TableCell className="text-center">{l.days}</TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate" title={l.reason}>{l.reason || "—"}</TableCell>
      <TableCell>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_CLS[l.status]}`}>{l.status}</span>
      </TableCell>
      <TableCell className="text-right">
        {allowDecide && l.status === "pending" && (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => approve.mutate(null)} disabled={approve.isPending} data-testid={`approve-${l.id}`}>
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => reject.mutate(null)} disabled={reject.isPending} data-testid={`reject-${l.id}`}>
              <X className="h-3.5 w-3.5 text-rose-600" />
            </Button>
          </div>
        )}
        {allowCancel && (l.status === "pending" || l.status === "approved") && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => cancel.mutate()} disabled={cancel.isPending} data-testid={`cancel-${l.id}`}>Cancel</Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function Loading() {
  return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>;
}
